import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { config, yup } from 'data';
import { formatter, url } from 'helpers';
import { authService } from 'services';
import { useCountdown, useForm, useLang, useMutation } from 'hooks';
import { PageLayout } from 'layouts';
import { Button, Flex, Form, Input, Paragraph, Progress, Title, Trans } from 'components/ui';
import { AuthPasswordResetConfirmParams } from 'types/services';

import styles from './styles.module.css';

type FormValues = AuthPasswordResetConfirmParams & {
  passwordConfirmation: string;
};

const initialValues: FormValues = {
  email: '',
  password: '',
  passwordConfirmation: '',
  code: '',
};

const validationSchema = yup.object({
  email: yup.string().required().email(),
  password: yup.string().defined(),
  passwordConfirmation: yup.string().defined(),
  code: yup.string().defined(),
});

const confirmationValidationSchema = validationSchema.shape({
  password: yup.string().required().password(),
  passwordConfirmation: yup.string().required().confirmation(yup.ref('password')),
  code: yup.string().required().trim().length(config.VERIFICATION_CODE_LENGTH),
});

const PasswordResetPage = () => {
  const countdown = useCountdown(config.VERIFICATION_CODE_RESEND_TIME);
  const form = useForm<FormValues>();
  const lang = useLang();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialValues.email);

  const resetPasswordMutation = useMutation({
    mutationFn: authService.resetPassword,
    onMutate: () => countdown.restart(),
    successNotification: lang.get('passwordReset.resetSuccess'),
  });

  const resetPasswordConfirmMutation = useMutation({
    mutationFn: authService.resetPasswordConfirm,
    onSuccess: () => navigate(url.toLogin()),
    successNotification: lang.get('passwordReset.confirmSuccess'),
  });

  const handleResendClick = async () => email && resetPasswordMutation.mutateAsync({ email });

  const handleBackClick = () => {
    form.resetFields(['code']);

    setEmail(initialValues.email);
  };

  const handleSubmit = async (values: FormValues) => {
    values.code
      ? await resetPasswordConfirmMutation.mutateAsync(values)
      : await resetPasswordMutation.mutateAsync(values);

    setEmail(values.email);

    return false;
  };

  const confirmation = Boolean(email);

  return (
    <PageLayout title={lang.get('passwordReset.title')}>
      <Progress className={styles.progress} percent={confirmation ? 100 : 50} />
      <Title>{confirmation ? lang.get('passwordReset.confirmHeading') : lang.get('passwordReset.heading')}</Title>
      <Paragraph>
        <Trans
          transKey={confirmation ? 'passwordReset.confirmCaption' : 'passwordReset.caption'}
          components={{
            Email: <strong>{email}</strong>,
            Link: <Link to={url.toLogin()} />,
          }}
          replace={{ codeLength: config.VERIFICATION_CODE_LENGTH, email }}
        />
      </Paragraph>
      <Form
        className={styles.form}
        form={form}
        initialValues={initialValues}
        validationSchema={confirmation ? confirmationValidationSchema : validationSchema}
        onFinish={handleSubmit}
      >
        <Form.Item name="email" hidden={confirmation}>
          <Input type="email" placeholder={lang.get('common.form.email.placeholder')} />
        </Form.Item>
        <Form.Item
          name="code"
          label={lang.get('common.form.code.label')}
          extra={
            countdown.counting
              ? (
                lang.get('common.form.resend.timer', {
                  time: formatter.formatTime(countdown.time, { showMinutes: true }),
                })
              ) : (
                <Button
                  className={styles.resend}
                  type="link"
                  loading={resetPasswordMutation.isPending}
                  onClick={handleResendClick}
                >
                  {lang.get('common.form.resend.button')}
                </Button>
              )
          }
          hidden={!confirmation}
        >
          <Input
            placeholder={lang.get('common.form.code.placeholder')}
            minLength={config.VERIFICATION_CODE_LENGTH}
            maxLength={config.VERIFICATION_CODE_LENGTH}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={lang.get('passwordReset.password.label')}
          hidden={!confirmation}
        >
          <Input type="password" placeholder={lang.get('passwordReset.password.placeholder')} />
        </Form.Item>
        <Form.Item
          name="passwordConfirmation"
          label={lang.get('passwordReset.passwordConfirmation.label')}
          hidden={!confirmation}
        >
          <Input type="password" placeholder={lang.get('passwordReset.passwordConfirmation.placeholder')} />
        </Form.Item>
        <Flex gap="small" justify="flex-end">
          <Button
            type="default"
            hidden={!confirmation}
            onClick={handleBackClick}
          >
            {lang.get('common.actions.back')}
          </Button>
          <Button
            htmlType="submit"
            loading={resetPasswordMutation.isPending || resetPasswordConfirmMutation.isPending}
          >
            {lang.get('common.actions.continue')}
          </Button>
        </Flex>
      </Form>
    </PageLayout>
  );
};

export default PasswordResetPage;
